import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { setProjectIdentity } from '@store/actionSlices/projectIdentity'
import { RouteKey } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import {
  selectFromResult as selectFromProjectConfigResult,
  useLazyGetConfigQuery,
} from '@api/config'

import ResetUtil from '@utilities/reset-util'
import useDefaultRoom from '@utilities/use-default-room'

import { Project } from './types'

const useProjectSwitchUtils = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const resetUtil = ResetUtil()

  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )
  const [getProjectConfig] = useLazyGetConfigQuery()

  const { getDefaultRoom, setDefaultRoom } = useDefaultRoom()

  const validateRedirectPath = useCallback<(path: string) => boolean>(
    (path) => {
      const routes = Object.values(RouteKey)
      return routes.includes(path as RouteKey)
    },
    []
  )

  const startProjectSwitch = useCallback<
    (args: { project: Project; redirectTo?: string }) => Promise<{
      status: boolean
      message?: string
    }>
  >(
    async ({ project, redirectTo }) => {
      const { uuid, canonicalName, name } = project

      const projectConfigResponse = await getProjectConfig({
        projectName: canonicalName,
      })
      const { configInfo } = selectFromProjectConfigResult(
        projectConfigResponse
      )

      const isActive = !!configInfo?.showcaseActive

      if (!isActive) {
        return {
          status: false,
          message: 'Project is not active',
        }
      }

      const defaultRoom = await getDefaultRoom({
        projectName: canonicalName,
        prioritizeCurrentRoom: true,
      })

      if (defaultRoom) {
        setDefaultRoom(defaultRoom)
      }

      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          projectId: uuid,
          projectName: canonicalName,
          projectLabel: name,
          assetPort: '',
          assetOptions: {
            localImages: false,
            localVideos: false,
          },
        })
      )
      resetUtil.resetLocalStorage()

      const isValidRedirectPath = redirectTo
        ? validateRedirectPath(redirectTo)
        : false

      if (redirectTo && isValidRedirectPath) {
        history.replace({ pathname: `/${canonicalName}/${redirectTo}` })
      }
      window.location.reload()

      return {
        status: true,
      }
    },
    [projectIdentity, getProjectConfig, getDefaultRoom, setDefaultRoom]
  )

  return {
    startProjectSwitch,
    validateRedirectPath,
  }
}

export default useProjectSwitchUtils
